import Badge from '@amzn/awsui-components-core/badge';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import Container from '@amzn/awsui-components-core/container';
import ContentLayout from '@amzn/awsui-components-core/content-layout';
import CopyToClipboard from '@amzn/awsui-components-core/copy-to-clipboard';
import Header from '@amzn/awsui-components-core/header';
import KeyValuePairs from '@amzn/awsui-components-core/key-value-pairs';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import Spinner from '@amzn/awsui-components-core/spinner';
import StatusIndicator from '@amzn/awsui-components-core/status-indicator';
import React from 'react';
import { useStarGridHealth } from '../hooks/useStarGridHealth';
import { useStarGridInfra } from '../hooks/useStarGridInfra';
import { STAR_GRID_API_BASE_URL } from '../starGridConstants';

/** Admin Tools → Infra Setup details. Live API health + the StarRocks descriptor. */
export const InfraSetupView: React.FC = () => {
  const { health, loading, error } = useStarGridHealth();
  const { infra, loading: infraLoading, error: infraError, refresh } = useStarGridInfra();

  // At-a-glance API health, shown in the container header so status reads before the details do.
  const healthIndicator = error ? (
    <StatusIndicator type="error">Down</StatusIndicator>
  ) : health && health.status !== 'ok' ? (
    <StatusIndicator type="warning">{health.status}</StatusIndicator>
  ) : (
    <StatusIndicator type="success">Alive</StatusIndicator>
  );

  return (
    <ContentLayout
      header={
        <Header variant="h1" description="Deployed StarGrid API + StarRocks cluster details (from SSM, served by the API).">
          Infra Setup details
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Container
          header={
            <Header variant="h2" description="Live health check of the deployed StarGrid API." actions={loading ? undefined : healthIndicator}>
              API status
            </Header>
          }
        >
          {loading ? (
            <Spinner size="large" />
          ) : (
            <KeyValuePairs
              columns={3}
              items={[
                { label: 'Health', value: healthIndicator },
                { label: 'Version', value: health?.version ? <Badge color="blue">{health.version}</Badge> : '—' },
                {
                  label: 'API endpoint',
                  value: (
                    <CopyToClipboard
                      variant="inline"
                      textToCopy={STAR_GRID_API_BASE_URL}
                      copyButtonAriaLabel="Copy API endpoint"
                      copySuccessText="API endpoint copied"
                      copyErrorText="Failed to copy API endpoint"
                    />
                  )
                }
              ]}
            />
          )}
        </Container>

        <Container
          header={
            <Header
              variant="h2"
              description="Cluster descriptor published to SSM by the API."
              actions={
                <Button iconName="refresh" onClick={refresh} loading={infraLoading}>
                  Refresh
                </Button>
              }
            >
              StarRocks infrastructure
            </Header>
          }
        >
          {infraLoading ? (
            <Spinner size="large" />
          ) : (
            <SpaceBetween size="m">
              {infraError && <StatusIndicator type="error">{infraError}</StatusIndicator>}
              {infra && !infra.available && <StatusIndicator type="pending">Not deployed yet — no StarRocks descriptor published.</StatusIndicator>}
              {infra?.available && infra.details && (
                <KeyValuePairs
                  columns={3}
                  items={Object.entries(infra.details).map(([key, value]) => ({
                    label: key,
                    value: typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value)
                  }))}
                />
              )}
              {infra?.available && !infra.details && <Box color="text-body-secondary">Descriptor published, but no details.</Box>}
            </SpaceBetween>
          )}
        </Container>
      </SpaceBetween>
    </ContentLayout>
  );
};

export default InfraSetupView;
